import React, { type ComponentProps, type MutableRefObject, type Ref } from 'react';
import { ShoppingCartOutlined } from '@ant-design/icons';
import { Card, Form, Radio, Space, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import OrderItemsForm from './OrderItemsForm';
import { ITEM_MODE_INPUT, ITEM_MODE_PRECISE, type ItemMode } from '@hooks/orders/useItemMode';

type OrderItemsFormProps = ComponentProps<typeof OrderItemsForm>;

// Props OrderItemsCard supplies internally — caller does not pass these.
type ManagedKeys = 'fields' | 'add' | 'remove' | 'mode';
type PassThroughProps = Omit<OrderItemsFormProps, ManagedKeys>;

interface OrderItemsCardProps extends PassThroughProps {
  itemMode: ItemMode;
  onModeChange: (mode: ItemMode) => void;
  showModeToggle?: boolean;
  /** When true, attaches a Form.List validator that requires at least one item. */
  required?: boolean;
  /** Optional ref attached to the card's title row — used as a Tour anchor. */
  titleAnchorRef?: Ref<HTMLDivElement>;
  /** Refs whose `.current` will be set to Form.List's add/remove. */
  addItemRef: MutableRefObject<((item: Record<string, unknown>) => void) | null>;
  removeItemRef: MutableRefObject<((index: number | number[]) => void) | null>;
}

const OrderItemsCard: React.FC<OrderItemsCardProps> = ({
  itemMode,
  onModeChange,
  showModeToggle = true,
  required = false,
  titleAnchorRef,
  addItemRef,
  removeItemRef,
  ...itemsFormProps
}) => {
  const { t } = useTranslation();

  const requiredRules = required
    ? [{
        validator: async (_: unknown, items: unknown[] | undefined) => {
          if (!items || items.length === 0) {
            return Promise.reject(new Error(t('common.validation.required') as string));
          }
          return Promise.resolve();
        },
      }]
    : undefined;

  return (
    <Card
      title={
        <div ref={titleAnchorRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <ShoppingCartOutlined />
            <span>{t('shared.orders.form.sections.products')}</span>
            {required && <Tag color="red">{t('common.validation.required')}</Tag>}
          </Space>
          {showModeToggle && (
            <Radio.Group
              value={itemMode}
              onChange={(e) => onModeChange(e.target.value as ItemMode)}
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value={ITEM_MODE_INPUT}>{t('shared.orders.form.item_mode.input')}</Radio.Button>
              <Radio.Button value={ITEM_MODE_PRECISE}>{t('shared.orders.form.item_mode.precise')}</Radio.Button>
            </Radio.Group>
          )}
        </div>
      }
      style={{ marginBottom: 24 }}
    >
      <Form.List name="order_items" rules={requiredRules}>
        {(fields, { add, remove }) => {
          addItemRef.current = add as unknown as (item: Record<string, unknown>) => void;
          removeItemRef.current = remove;
          return (
            <OrderItemsForm
              {...itemsFormProps}
              fields={fields}
              add={add}
              remove={remove}
              mode={itemMode}
            />
          );
        }}
      </Form.List>
    </Card>
  );
};

export default OrderItemsCard;
